import axios from "axios";
import { toast } from "react-toastify";

// Determine the correct Node.js backend URL based on environment
const getNodeBackendURL = () => {
  const hostname = window.location.hostname;

  // Keep local development isolated from the deployed Node backend.
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return process.env.REACT_APP_NODE_BACKEND_URL || "http://localhost:5000";
  }

  // If we're in staging environment, use staging Node.js backend
  if (hostname.includes("staging")) {
    return "https://node-backend-staging-604521917673.asia-northeast1.run.app";
  }

  // Always use production Node.js backend (not local development)
  // This ensures Dialogflow and course data come from the deployed backend

  // Production environment
  return "https://node-backend-604521917673.asia-northeast1.run.app";
};

const BASE_URL = getNodeBackendURL();

// Create an axios instance for your Node.js backend API
const apiNode = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

// Request interceptor to add the JWT token and X-Tenant-Domain header
apiNode.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    const tenantDomain = window.location.hostname; // Dynamically extract current subdomain

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    if (tenantDomain) {
      config.headers["X-Tenant-Domain"] = tenantDomain;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor to handle errors and token refresh logic
apiNode.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      // The Node service has no token refresh. This used to post the Django refresh
      // token to a Node route that doesn't exist, then delete that token and reload,
      // which signed the student out a few minutes later. A 401 here is only this call.
      return Promise.reject(error);
    } else {
      // Don't show toast errors for normal "not found" responses
      const errorMessage = error.response?.data?.message || error.response?.data?.detail;
      const isNormalNotFound = error.response?.status === 404 && (
        errorMessage === "No assessment session found for this user" ||
        errorMessage?.includes("No assessment session found") ||
        error.response?.data?.found === false ||
        error.config?.url?.includes('/get-assessment-session/') ||
        error.config?.url?.includes('/get-assessment-progress/') ||
        error.config?.url?.includes('/get-latest-dass42/') ||
        error.config?.url?.includes('/get-latest-ryff/') ||
        error.config?.url?.includes('/get-latest-riasec/') ||
        error.config?.url?.includes('/get-latest-ppca/')
      );
      
      // Callers that show their own message pass { skipErrorToast: true }, as with api.js
      if (!isNormalNotFound && !error.config?.skipErrorToast) {
        toast.error(
          errorMessage || "An error occurred. Please try again.",
        );
      }
      return Promise.reject(error);
    }
  },
);

export default apiNode;
